import { del, get, post } from './httpClient';

/**
 * Invite a mi espacio (v0) + perfil `space_name`.
 * Contrato: argenlotapi `docs/frontend/FRONTEND_ESPACIOS_INVITE.md`.
 * No cablear `getmyteam` / Mi equipo.
 * No usar `brand_name` para labels de espacio.
 */

const SPACE_NAME_MAX = 80;

/**
 * `{ owner_user_id }` si hay owner ajeno, `{}` si no. Sirve para la query de
 * las lecturas en espejo (F1) y para la query o el body de las escrituras
 * aditivas del invitado (F2). Sin owner la request sale igual que siempre
 * (propio espacio).
 *
 * @param {string|null|undefined} ownerUserId Cognito `sub` del owner del espacio activo.
 * @returns {{ owner_user_id?: string }}
 */
export function spaceOwnerQueryParams(ownerUserId) {
  const owner = ownerUserId ? String(ownerUserId).trim() : '';
  return owner ? { owner_user_id: owner } : {};
}

/**
 * POST /invitespacemember `{ email }`. Solo owner (JWT).
 * 201 alta / 200 idempotente. 400 self-invite / email inválido.
 *
 * @param {{ email: string, role?: 'editor' }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function inviteSpaceMember(body, { signal } = {}) {
  const email = String(body?.email || '').trim().toLowerCase();
  const payload = { email };
  if (body?.role) payload.role = body.role;
  return post('/invitespacemember', { body: payload, signal });
}

/**
 * GET /getspacemembers — miembros del espacio del JWT (dueño).
 * Envelope: `{ data: { owner_user_id, space_name, owner_display_name, owner_email, members }, total }`.
 *
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function getSpaceMembers({ signal } = {}) {
  return get('/getspacemembers', { signal });
}

/**
 * DELETE /deletespacemember?email= (preferido) o ?user_id=.
 * Solo owner. No borra editores por lista/showroom.
 *
 * @param {{ email?: string, user_id?: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function deleteSpaceMember(body, { signal } = {}) {
  const email = String(body?.email || '').trim().toLowerCase();
  const userId = String(body?.user_id || '').trim();
  return del('/deletespacemember', {
    params: {
      ...(email ? { email } : {}),
      ...(!email && userId ? { user_id: userId } : {}),
    },
    signal,
  });
}

/**
 * GET /getsharedwithmespaces — espacios donde el JWT es invitado.
 * Envelope: `{ data: { spaces: [...] }, total }`.
 * Cada space trae `space_name`, `owner_display_name`, `owner_email`.
 *
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function getSharedWithMeSpaces({ signal } = {}) {
  return get('/getsharedwithmespaces', { signal });
}

/**
 * GET /getmyspace — perfil del propio espacio (JWT = owner).
 * `data`: `{ owner_user_id, space_name, owner_display_name, owner_email, created_at, updated_at }`.
 *
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function getMySpace({ signal } = {}) {
  return get('/getmyspace', { signal });
}

/**
 * POST /upsertmyspace `{ space_name }`.
 * String vacío → limpia (`null`). Máx. 80 caracteres.
 *
 * @param {{ space_name: string }} body
 * @param {{ signal?: AbortSignal }} [options]
 */
export async function upsertMySpace(body, { signal } = {}) {
  const spaceName = body?.space_name == null ? '' : String(body.space_name);
  return post('/upsertmyspace', {
    body: { space_name: spaceName },
    signal,
  });
}

/**
 * Label UI del espacio (chrome / switcher / Personas).
 * 1) `space_name` si hay texto.
 * 2) Si no: `Espacio de {owner_display_name|owner_email}`.
 * No usa `brand_name`.
 *
 * @param {{
 *   space_name?: string|null,
 *   owner_display_name?: string|null,
 *   owner_email?: string|null,
 *   owner_user_id?: string,
 * }} space
 * @returns {string}
 */
export function getSpaceOwnerLabel(space) {
  const spaceName = String(space?.space_name || '').trim();
  if (spaceName) return spaceName;

  const display = String(space?.owner_display_name || '').trim();
  if (display) return `Espacio de ${display}`;

  const email = String(space?.owner_email || '').trim();
  if (email) return `Espacio de ${email}`;

  const id = String(space?.owner_user_id || '').trim();
  return id ? `Espacio ${id.slice(0, 8)}…` : 'Espacio compartido';
}

/** @deprecated alias — preferí `getSpaceOwnerLabel` */
export const getSpaceLabel = getSpaceOwnerLabel;

/**
 * Normaliza `data.members` del GET members.
 * @param {object} response
 * @returns {object[]}
 */
export function readSpaceMembers(response) {
  const members = response?.data?.members;
  return Array.isArray(members) ? members : [];
}

/**
 * Perfil / meta del espacio desde getspacemembers o getmyspace (`data`).
 * @param {object} response
 * @returns {{ owner_user_id?: string, space_name?: string|null, owner_display_name?: string|null, owner_email?: string|null }}
 */
export function readSpaceProfile(response) {
  const data = response?.data;
  if (!data || typeof data !== 'object' || Array.isArray(data)) return {};
  return {
    owner_user_id: data.owner_user_id,
    space_name: data.space_name ?? null,
    owner_display_name: data.owner_display_name ?? null,
    owner_email: data.owner_email ?? null,
    created_at: data.created_at ?? null,
    updated_at: data.updated_at ?? null,
  };
}

/**
 * Normaliza `data.spaces` del GET shared-with-me.
 * @param {object} response
 * @returns {object[]}
 */
export function readSharedSpaces(response) {
  const spaces = response?.data?.spaces;
  return Array.isArray(spaces) ? spaces : [];
}

export { SPACE_NAME_MAX };
